(ns machtfit.budget-periods
  (:require [cljs-time.core :as time]
            [clojure.string :as str]
            [com.rpl.specter :as sp]
            [machtfit.company-editor-styles :as styles]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.datetime :as datetime]
            [machtfit.human :as human]
            [machtfit.utils :as utils]
            [machtfit.widgets.checkbox :as checkbox]
            [re-frame.core :as rf]))

(defn ^:export load [js-data]
  (rf/dispatch [::init (js->clj js-data :keywordize-keys true)]))

(defn- init-budget-periods [db]
  (update-in db [:data :budget-periods]
             #(->> %
                   (sp/transform [sp/ALL :start-date] datetime/iso-date->date-string)
                   (sp/transform [sp/ALL :end-date] datetime/iso-date->date-string))))

(defn- init [db data]
  (-> db
      (merge data)
      (init-budget-periods)))

(def default-value-string "–––")

(def ^:private secondary-button-classes
  ["inline-flex" "cursor-pointer" "items-center" "gap-2" "rounded-lg" "border" "border-gray-300"
   "bg-white" "px-4" "py-2.5" "text-sm" "font-semibold" "text-gray-700" "transition-colors" "hover:bg-gray-50"])

(def ^:private danger-button-classes
  ["inline-flex" "cursor-pointer" "items-center" "gap-2" "rounded-lg" "bg-error-600" "px-4" "py-2.5"
   "text-sm" "font-semibold" "text-white" "shadow-xs" "transition-colors" "hover:bg-error-700"])

(def ^:private amount-input-classes
  ["w-28" "rounded-lg" "border" "border-gray-200" "bg-white" "px-3" "py-2" "text-right" "text-sm"
   "tabular-nums" "text-gray-900" "transition-colors" "focus:border-brand-400" "focus:outline-none"
   "focus:ring" "focus:ring-brand-500/15"])

;; widgets/input, not inline-input, whose forced 4rem width clips these values.
(def ^:private condition-input-classes
  ["w-full" "max-w-[180px]" "rounded-lg" "border" "border-gray-200" "bg-white" "px-2.5" "py-1.5" "text-sm"
   "text-gray-900" "transition-colors" "focus:border-brand-400" "focus:outline-none"
   "focus:ring" "focus:ring-brand-500/15"])

;; widgets/select-box takes no :class, so style edit-mode <select>s via descendant variants.
(def ^:private edit-control-variant-classes
  ["[&_select]:rounded-lg" "[&_select]:border" "[&_select]:border-gray-200" "[&_select]:bg-white"
   "[&_select]:px-2.5" "[&_select]:py-1.5" "[&_select]:text-sm" "[&_select]:text-gray-900"])

(rf/reg-event-db ::init
  (fn [db [_ data]]
    (init db data)))

(rf/reg-fx ::alert
  (fn [text]
    (js/alert text)))

(rf/reg-sub ::num-rules
  (fn [db [_ budget-period-index]]
    (count (get-in db [:data :budget-periods budget-period-index :budget-rules]))))

(rf/reg-sub ::rule-by-index
  (fn [db [_ budget-period-index index]]
    (get-in db [:data :budget-periods budget-period-index :budget-rules index])))

(defn- rule-condition-values [rules property]
  (remove nil? (for [rule rules] (get-in rule [:condition :employee property]))))

(rf/reg-sub ::employee-group-by-id
  :<- [:get [:data :employee-groups]]

  (fn [employee-groups [_ id]]
    (utils/by-id employee-groups id)))

(rf/reg-sub ::places-of-business-by-id
  :<- [:get [:data :places-of-business]]

  (fn [places-of-business [_ id]]
    (utils/by-id places-of-business id)))

(rf/reg-sub ::employment-type-by-key
  :<- [:get [:data :employment-types]]

  (fn [employment-types [_ key]]
    (get employment-types (keyword key))))

(rf/reg-event-db ::move-rule-up
  (fn [db [_ budget-period-index index]]
    (-> db
        (update-in [:data :budget-periods budget-period-index :budget-rules] utils/move-up index)
        (update-in [:ui :editing budget-period-index :rule] utils/decrease-index))))

(rf/reg-event-db ::move-rule-down
  (fn [db [_ budget-period-index index]]
    (-> db
        (update-in [:data :budget-periods budget-period-index :budget-rules] utils/move-down index)
        (update-in [:ui :editing budget-period-index :rule] utils/increase-index))))

(rf/reg-event-db ::add-rule
  (fn [db [_ budget-period-index]]
    (let [new-rule {:amount 0 :condition {}}
          add-rule #(conj (if (empty? %) [] %) new-rule)]
      (-> db
          (update-in [:data :budget-periods budget-period-index :budget-rules] add-rule)
          (assoc-in [:ui :editing] {budget-period-index {:rule (count (get-in db [:data :budget-periods budget-period-index :budget-rules]))}})))))

(rf/reg-event-db ::edit-rule
  (fn [db [_ budget-period-index index]]
    (assoc-in db [:ui :editing budget-period-index] {:rule index})))

(rf/reg-event-db ::delete-rule
  (fn [db [_ budget-period-index index]]
    (update-in db [:data :budget-periods budget-period-index :budget-rules] utils/delete-at index)))

(rf/reg-sub ::exists-employee-condition?
  (fn [db [_ budget-period-index]]
    (let [rules (get-in db [:data :budget-periods budget-period-index :budget-rules])
          occupation-groups (rule-condition-values rules :occupation-group)
          places-of-business (rule-condition-values rules :place-of-business)
          subsidiaries (rule-condition-values rules :subsidiary)
          employee-group (rule-condition-values rules :employee-group)
          weekly-work-hours-greater-than-or-equal (rule-condition-values rules :weekly-work-hours-greater-than-or-equal)
          employment-type (rule-condition-values rules :employment-type)]
      (not-every? empty? [occupation-groups places-of-business subsidiaries employee-group weekly-work-hours-greater-than-or-equal employment-type]))))

(defn- rule-condition-employee [budget-period-index
                                {:keys [occupation-group
                                        place-of-business
                                        subsidiary
                                        employee-group
                                        employment-type
                                        weekly-work-hours-greater-than-or-equal
                                        employment-started-on-or-before]}]
  [:span
   (when occupation-group
     (str " Berufsgruppe: " occupation-group))
   (when place-of-business
     (str " Standort: " (:name @(rf/subscribe [::places-of-business-by-id place-of-business]))))
   (when subsidiary
     (str " Unternehmensteil: " subsidiary))
   (when employee-group
     (str " Mitarbeitergruppe: " (:name @(rf/subscribe [::employee-group-by-id employee-group]))))
   (when employment-type
     (str " Beschäftigungsart: " @(rf/subscribe [::employment-type-by-key employment-type])))
   (when weekly-work-hours-greater-than-or-equal
     (str " Wochenarbeitsstunden ab: " weekly-work-hours-greater-than-or-equal))
   (when employment-started-on-or-before
     (str " eingestellt vor oder am: " employment-started-on-or-before))
   (when-not (or occupation-group
                 place-of-business
                 subsidiary
                 employee-group
                 weekly-work-hours-greater-than-or-equal
                 employment-type employment-started-on-or-before)
     (str (if @(rf/subscribe [::exists-employee-condition? budget-period-index]) "restliche" "alle") " Mitarbeiter"))])

(defn- edit-rule-occupation-group [budget-period-index index]
  (let [rule @(rf/subscribe [::rule-by-index budget-period-index index])
        condition-path [:data :budget-periods budget-period-index :budget-rules index :condition :employee :occupation-group]]
    [:tr
     [:td "Berufsgruppe:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee :occupation-group) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (if (= default-value-string %) nil %)])
            :data (into [default-value-string]
                        @(rf/subscribe [:get [:data :occupation-groups]]))}]]]))

(defn- edit-rule-place-of-business [budget-period-index index]
  (let [rule @(rf/subscribe [::rule-by-index budget-period-index index])
        condition-path [:data :budget-periods budget-period-index :budget-rules index :condition :employee :place-of-business]]
    [:tr
     [:td "Standort:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee :place-of-business) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (when (not= default-value-string %)
                                                                 (int %))])
            :data (into [{:value default-value-string :label default-value-string}]
                        (map #(identity {:value (:id %) :label (:name %)})
                             @(rf/subscribe [:get [:data :places-of-business]])))}]]]))

(defn- edit-rule-subsidiary [budget-period-index index]
  (let [rule @(rf/subscribe [::rule-by-index budget-period-index index])
        condition-path [:data :budget-periods budget-period-index :budget-rules index :condition :employee :subsidiary]]
    [:tr
     [:td "Unternehmensteil:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee :subsidiary) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (if (= default-value-string %) nil %)])
            :data (into [default-value-string]
                        @(rf/subscribe [:get [:data :subsidiaries]]))}]]]))

(defn- edit-rule-employee-group [budget-period-index index]
  (let [rule @(rf/subscribe [::rule-by-index budget-period-index index])
        condition-path [:data :budget-periods budget-period-index :budget-rules index :condition :employee :employee-group]]
    [:tr
     [:td "Mitarbeitergruppe:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee :employee-group) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (if (= default-value-string %) nil (int %))])
            :data (into [{:value default-value-string :label default-value-string}]
                        (map #(identity {:value (:id %) :label (str (:name %) " (" (:type %) ")")})
                             @(rf/subscribe [:get [:data :employee-groups]])))}]]]))

(defn- edit-rule-employment-type [budget-period-index index]
  (let [rule @(rf/subscribe [::rule-by-index budget-period-index index])
        condition-path [:data :budget-periods budget-period-index :budget-rules index :condition :employee :employment-type]]
    [:tr
     [:td "Beschäftigungsart:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee :employment-type) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (if (= default-value-string %) nil %)])
            :data (into [{:value default-value-string :label default-value-string}]
                        (map (fn [[k v]] {:value k :label v})
                             @(rf/subscribe [:get [:data :employment-types]])))}]]]))

(defn- edit-rule-employment-started-on-or-before [budget-period-index index]
  (let [rule @(rf/subscribe [::rule-by-index budget-period-index index])
        condition-path [:data :budget-periods budget-period-index :budget-rules index :condition :employee :employment-started-on-or-before]]
    [:tr
     [:td "eingestellt vor oder am:"]
     [:td [widgets/input {:value (-> rule :condition :employee :employment-started-on-or-before)
                          :class condition-input-classes
                          :style {:width nil}
                          :on-change #(rf/dispatch [:set-path condition-path (str/replace % #"[^0-9\.]" "")])}]]]))

(defn- edit-rule-weekly-work-hours-greater-than-or-equal [budget-period-index index]
  (let [rule @(rf/subscribe [::rule-by-index budget-period-index index])
        condition-path [:data :budget-periods budget-period-index :budget-rules index :condition :employee :weekly-work-hours-greater-than-or-equal]]
    [:tr
     [:td "Wochenarbeitsstunden ab:"]
     [:td [widgets/input {:value (-> rule :condition :employee :weekly-work-hours-greater-than-or-equal)
                          :type widgets/type-positive-number
                          :class condition-input-classes
                          :style {:width nil}
                          :on-save #(rf/dispatch [:set-path condition-path %])}]]]))

(defn- edit-rule-conditions-employee [budget-period-index index]
  ;; Space the condition rows out and keep the label column from crowding the inputs (QA #7).
  [:table {:class ["[&_td]:py-1.5" "[&_td:first-child]:pr-4" "[&_td:first-child]:whitespace-nowrap" "[&_td:first-child]:align-middle"]}
   [:tbody
    [edit-rule-occupation-group budget-period-index index]
    [edit-rule-place-of-business budget-period-index index]
    [edit-rule-subsidiary budget-period-index index]
    [edit-rule-employee-group budget-period-index index]
    [edit-rule-employment-type budget-period-index index]
    [edit-rule-weekly-work-hours-greater-than-or-equal budget-period-index index]
    [edit-rule-employment-started-on-or-before budget-period-index index]]])

(defn- rule [budget-period-index index]
  (let [#_:clj-kondo/ignore
        rule @(rf/subscribe [::rule-by-index budget-period-index index])
        prev-rule @(rf/subscribe [::rule-by-index budget-period-index (dec index)])
        next-rule @(rf/subscribe [::rule-by-index budget-period-index (inc index)])
        product-id (-> rule :condition :product :id)
        prev-product-id (-> prev-rule :condition :product :id)
        next-product-id (-> next-rule :condition :product :id)
        top-reached? (>= (inc index) @(rf/subscribe [::num-rules budget-period-index]))
        bottom-reached? (= 0 index)
        editing? (= index @(rf/subscribe [:get [:ui :editing budget-period-index :rule]]))]
    [:tr {:class ["border-t" "border-gray-100"]}
     [:td {:class (conj styles/td "w-px")}
      [:div {:class ["flex" "items-center" "gap-1"]}
       (when (and (not top-reached?) (= next-product-id product-id))
         [:button {:type "button"
                   :class styles/reorder-button
                   :aria-label "Regel nach unten verschieben"
                   :on-click #(rf/dispatch [::move-rule-down budget-period-index index])}
          [styles/arrow-icon true]])
       (when (and (not bottom-reached?) (= prev-product-id product-id))
         [:button {:type "button"
                   :class styles/reorder-button
                   :aria-label "Regel nach oben verschieben"
                   :on-click #(rf/dispatch [::move-rule-up budget-period-index index])}
          [styles/arrow-icon false]])]]
     [:td {:class (conj styles/td "text-gray-900")}
      (if editing?
        [edit-rule-conditions-employee budget-period-index index]
        [rule-condition-employee budget-period-index (get-in rule [:condition :employee])])]
     [:td {:class styles/td}
      [:div {:class ["flex" "items-center" "gap-1.5"]}
       [widgets/input
        ;; :style {:width nil} unsets type-amount's fixed inline :width so the Tailwind w-28 in
        ;; amount-input-classes drives the sizing instead.
        {:value (:amount rule)
         :type widgets/type-amount
         :class amount-input-classes
         :style {:width nil}
         :on-save #(rf/dispatch [:set-path [:data :budget-periods budget-period-index :budget-rules index :amount] %])
         :validate-virgin? true}]
       [:span {:class ["text-sm" "text-gray-400"]} "€"]]]
     [:td {:class (conj styles/td "whitespace-nowrap" "text-right")}
      [:div {:class ["flex" "items-center" "justify-end" "gap-2"]}
       (when-not editing?
         [:button {:type "button"
                   :class styles/edit-action
                   :on-click #(rf/dispatch [::edit-rule budget-period-index index])} "Bearbeiten"])
       (when editing?
         [:button {:type "button"
                   :class styles/done-action
                   :on-click #(rf/dispatch [::edit-rule budget-period-index nil])} "Fertig"])
       [:button {:type "button"
                 :class styles/delete-action
                 :on-click #(rf/dispatch [::delete-rule budget-period-index index])} "Löschen"]]]]))

(defn- rules [budget-period-index]
  [:div {:class (conj styles/card "mt-4")}
   [:div {:class ["border-b" "border-gray-100" "px-6" "py-4"]}
    [:h3 {:class ["text-base" "font-semibold" "text-gray-900"]} "Guthabenregeln"]
    [:p {:class ["mt-0.5" "text-xs" "text-gray-500"]}
     "bitte die Reihenfolge beachten, der erste Treffer gilt"]]
   [:div {:class ["overflow-x-auto"]}
    [:table {:class ["w-full" "text-left" "text-sm"]}
     [:thead
      [:tr
       [:th {:class styles/th}]
       [:th {:class styles/th} "Falls Mitarbeiter"]
       [:th {:class styles/th} "Guthaben"]
       [:th {:class styles/th}]]]
     (into [:tbody]
           (for [[index budget-rule] (map-indexed vector @(rf/subscribe [:get [:data :budget-periods budget-period-index :budget-rules]]))]
             ^{:key index}
             [:<>
              [rule budget-period-index index]
              (when (seq (:errors budget-rule))
                [:tr
                 [:td]
                 [:td {:col-span 3 :class ["px-4" "pb-3"]}
                  (into [:ul {:class ["flex" "flex-col" "gap-1" "text-sm" "text-error-600"]}]
                        (for [[idx error] (map-indexed vector (:errors budget-rule))]
                          ^{:key idx} [:li error]))]])]))]]
   [:div {:class ["border-t" "border-gray-100" "px-6" "py-4"]}
    [:button {:type "button"
              :class styles/ghost-button
              :on-click #(rf/dispatch [::add-rule budget-period-index])}
     "+ Regel hinzufügen"]]])

(rf/reg-sub ::budget-period-by-index
  (fn [db [_ index]]
    (get-in db [:data :budget-periods index])))

(rf/reg-sub ::budget-period-count
  :<- [:get [:data :budget-periods]]

  (fn [budget-periods _]
    (count budget-periods)))

(defn- budget-valid? [{:keys [start-date end-date budget-rules]}]
  (and
   (-> start-date datetime/string->date some?)
   (-> end-date datetime/string->date some?)
   (seq budget-rules)))

(rf/reg-sub ::budget-period-valid?
  (fn [_ [_ index]]
    (budget-valid? @(rf/subscribe [::budget-period-by-index index]))))

(rf/reg-event-db ::edit-budget-period
  (fn [db [_ budget-period-index]]
    (assoc-in db [:ui :editing] {budget-period-index {}})))

(defn- edit-budget-period-button [index]
  [:button {:type "button"
            :class secondary-button-classes
            :on-click #(rf/dispatch [::edit-budget-period index])}
   "Bearbeiten"])

(rf/reg-event-fx ::delete-budget-period
  (fn [{:keys [db]} [_ index]]
    (if (nil? (get-in db [:data :budget-periods index :id]))
      {:db (update-in db [:data :budget-periods] #(vec (concat (subvec % 0 index) (subvec % (inc index)))))}
      {:post {:url (get-in db [:data :budget-periods index :delete-url])
              :csrf-token (get-in db [:io :csrf-token])
              :on-success #(rf/dispatch [::save-budget-period-success index %])
              :on-error #(rf/dispatch [::save-budget-period-error])}})))

(defn- delete-budget-period-button [index]
  [:button {:type "button"
            :class danger-button-classes
            :on-click #(rf/dispatch [::delete-budget-period index])}
   "Löschen"])

(rf/reg-event-fx ::save-budget-period-success
  (fn [{:keys [db]} [_ index result]]
    (if (:success result)
      {:redirect {:url (:redirect-url result)}}
      (let [errors (:errors result)]
        {:db (update-in db [:data :budget-periods index] (fn [budget-period]
                                                           (cond-> budget-period
                                                             (:errors errors) (assoc :errors (:errors errors))
                                                             (:budget-rules errors) (assoc :budget-rules (vec (map-indexed (fn [index budget-rule]
                                                                                                                             (if-let [budget-rules-error (get-in errors [:budget-rules (keyword (str index))])]
                                                                                                                               (assoc budget-rule :errors budget-rules-error)
                                                                                                                               budget-rule)) (:budget-rules budget-period)))))))}))))

(rf/reg-event-fx ::save-budget-period-error
  (fn [_ [_ _]]
    {::alert "Das hat leider nicht geklappt. Bitte wende dich an das Produkt-Team."}))

(rf/reg-event-fx ::save-budget-period
  (fn [{:keys [db]} [_ index]]
    (let [budget-period (get-in db [:data :budget-periods index])
          to-date #(-> % datetime/date-string->utc-date .-date)
          to-number #(->> %
                          human/human->amount
                          human/amount->cents)
          data (->> budget-period
                    (sp/transform [:start-date] to-date)
                    (sp/transform [:end-date] to-date))
          data (update data :budget-rules (fn [budget-rules] (map-indexed #(assoc %2 :index %1 :amount (to-number (:amount %2))) budget-rules)))
          ;; clear all error messages from budged-periods and budged-rules
          db (update-in db [:data :budget-periods index] (fn [budget-period]
                                                           (assoc budget-period :errors nil :budget-rules (map #(assoc % :errors nil) (:budget-rules budget-period)))))]
      {:post {:url (get-in db [:io :save-url])
              :csrf-token (get-in db [:io :csrf-token])
              :data (clj->js data)
              :on-success #(rf/dispatch [::save-budget-period-success index %])
              :on-error #(rf/dispatch [::save-budget-period-error])}})))

(defn- save-budget-period-button [index]
  (let [valid? @(rf/subscribe [::budget-period-valid? index])]
    [:button {:type "button"
              :class styles/primary-button
              :disabled (not valid?)
              :on-click #(when valid? (rf/dispatch [::save-budget-period index]))}
     "Speichern"]))

(rf/reg-event-db ::toggle-recalculate
  (fn [db [_ index]]
    (update-in db [:data :budget-periods index :recalculate?] not)))

(defn- budget-period-row [index]
  (let [{:keys [id
                deleted?
                start-date
                end-date
                delete-url
                recalculate?
                errors]} @(rf/subscribe [::budget-period-by-index index])
        editing? @(rf/subscribe [:get [:ui :editing index]])
        show-recalculate-checkbox? @(rf/subscribe [:get [:data :show-recalculate-checkbox?]])
        can-delete? (or (nil? id) (some? delete-url))
        date-input-classes ["rounded-lg" "border" "border-gray-200" "bg-white" "px-2.5" "py-1.5" "text-sm" "text-gray-900"]
        field-start-date (if editing?
                           [widgets/inline-date-input
                            {:value start-date
                             :class date-input-classes
                             :style {:width "9rem" :text-align "left"}
                             :on-save #(rf/dispatch [:set-path [:data :budget-periods index :start-date] %])
                             :date-info? false
                             :validate-virgin? true}]
                           [:span {:class (when deleted? ["line-through"])} (-> start-date
                                                                                datetime/string->date
                                                                                human/dateformat-long)])
        field-end-date (if editing?
                         [widgets/inline-date-input
                          {:value end-date
                           :class date-input-classes
                           :style {:width "9rem" :text-align "left"}
                           :on-save #(rf/dispatch [:set-path [:data :budget-periods index :end-date] %])
                           :date-info? false
                           :validate-virgin? true}]
                         [:span {:class (when deleted? ["line-through"])} (-> end-date
                                                                              datetime/string->date
                                                                              human/dateformat-long)])]
    [:div {:class (into (conj styles/card "p-5") edit-control-variant-classes)}
     [:div {:class ["flex" "flex-wrap" "items-center" "justify-between" "gap-3"]}
      [:div {:class ["flex" "items-center" "gap-2" "text-base" "font-semibold" "text-gray-900"]}
       field-start-date
       [:span {:class ["text-gray-400"]} "–"]
       field-end-date]
      (when-not editing?
        [:div {:class ["flex" "items-center" "gap-2.5"]}
         [edit-budget-period-button index]
         (when can-delete?
           [delete-budget-period-button index])])]
     (when editing?
       [:<>
        [rules index]
        (when (seq errors)
          (into [:ul {:class ["mt-3" "flex" "flex-col" "gap-1" "text-sm" "text-error-600"]}]
                (for [[idx error] (map-indexed vector errors)]
                  ^{:key idx} [:li error])))
        [:div {:class ["mt-4" "flex" "flex-wrap" "items-center" "gap-2.5"]}
         (when show-recalculate-checkbox?
           [checkbox/checkbox
            {:label "Neu berechnen"
             :checked? recalculate?
             :on-click #(rf/dispatch [::toggle-recalculate index])}])
         [save-budget-period-button index]
         (when can-delete?
           [delete-budget-period-button index])]])]))

(rf/reg-event-db ::add-budget-period-budget
  (fn [db _]
    (let [current-year (time/year (time/time-now))
          new-index @(rf/subscribe [::budget-period-count])]
      (-> db
          (update-in [:data :budget-periods] (fnil conj []) {:start-date (str "01.01." current-year)
                                                             :end-date (str "31.12." current-year)})
          (assoc-in [:ui :editing] {new-index {}})))))

(defn- add-budget-period-button []
  [:button {:type "button"
            :class secondary-button-classes
            :on-click #(rf/dispatch [::add-budget-period-budget])}
   "+ Guthaben-Zeitraum hinzufügen"])

(defn- budget-periods-list []
  [:div {:class ["flex" "flex-col" "items-start" "gap-4"]}
   (into [:div {:class ["flex" "w-full" "flex-col" "gap-4"]}]
         (for [index (range @(rf/subscribe [::budget-period-count]))]
           ^{:key index} [budget-period-row index]))
   [add-budget-period-button]])

(rf/reg-event-fx ::toggle-auto-extension
  (fn [{:keys [db]} _]
    (when-not (get-in db [:ui :saving-auto-extension?])
      {:db (assoc-in db [:ui :saving-auto-extension?] true)
       :post {:url (get-in db [:io :save-auto-extension-url])
              :csrf-token (get-in db [:io :csrf-token])
              :data (clj->js {:enabled? (not (get-in db [:data :auto-extension-enabled?]))})
              :on-success #(rf/dispatch [::toggle-auto-extension-success %])
              :on-error #(rf/dispatch [::toggle-auto-extension-error])}})))

(rf/reg-event-fx ::toggle-auto-extension-success
  (fn [{:keys [db]} [_ result]]
    (let [db (assoc-in db [:ui :saving-auto-extension?] false)]
      (if (:success result)
        {:db (assoc-in db [:data :auto-extension-enabled?] (:enabled? result))}
        {:db db
         ::alert "Das hat leider nicht geklappt. Bitte wende dich an das Produkt-Team."}))))

(rf/reg-event-fx ::toggle-auto-extension-error
  (fn [{:keys [db]} _]
    {:db (assoc-in db [:ui :saving-auto-extension?] false)
     ::alert "Das hat leider nicht geklappt. Bitte wende dich an das Produkt-Team."}))

(defn- auto-extension-setting []
  [:div {:class (conj styles/card "p-5")}
   [checkbox/checkbox
    {:label "Budget automatisch verlängern"
     :hint (str "Der zuletzt ablaufende Guthaben-Zeitraum wird vor seinem Ende automatisch "
                "um ein Jahr verlängert. Änderungen werden sofort gespeichert.")
     :checked? @(rf/subscribe [:get [:data :auto-extension-enabled?]])
     :disabled? @(rf/subscribe [:get [:ui :saving-auto-extension?]])
     :on-click #(rf/dispatch [::toggle-auto-extension])}]])

(defn- heading []
  [:div
   [:h2 {:class ["text-base" "font-semibold" "text-gray-900"]} "Guthaben-Zeiträume"]
   [:p {:class ["mt-1" "max-w-3xl" "text-sm" "text-gray-500"]}
    (str "In der Guthabenverwaltung werden Guthaben in definierten Guthaben-Zeiträumen vergeben. "
         "Dabei können Guthabenregeln festgelegt werden, die steuern, wie das Guthaben innerhalb "
         "eines Zeitraums zugeteilt wird. Die Reihenfolge der Guthabenregeln ist flexibel und kann "
         "angepasst werden, um eine priorisierte Vergabe entsprechend den spezifischen Anforderungen "
         "sicherzustellen.")]])

(defn main []
  [:div {:class ["flex" "flex-col" "gap-5"]}
   [heading]
   [auto-extension-setting]
   [budget-periods-list]])
